// ============================================================
//  Abdeckung eines Identitätsschlüssels — Zahlen statt Erzähltext:
//  Wie viele fachliche Konten tragen den Schlüssel überhaupt, wie oft ist er mehrdeutig
//  (Kollision), wie viele Personen und FUE ergibt die Auflösung damit?
// ============================================================
import type { Account, IdentityKey, Person } from "./types";
import { normalize, resolveIdentity } from "./identity";
import { DEFAULT_RATIO, type RatioTable } from "./rules";

export interface IdentityCoverage {
  key: IdentityKey;
  /** Fachliche Konten (personId „P-…"), technische User bleiben außen vor (I7). */
  accounts: number;
  /** Konten, die unter diesem Schlüssel einen Wert tragen. */
  withKey: number;
  /** withKey / accounts in Prozent (0 bei leerem Bestand). */
  coveragePct: number;
  /** Schlüsselwerte, die auf mehr als eine Person zeigen — ungekappt gezählt (resolveIdentity listet höchstens 200). */
  collisions: number;
  /** Konten in Kollisionen (zählen einzeln, I13/I14). */
  collisionAccounts: number;
  recognizedPersons: number;
  fueTotal: number;
}

function keyRaw(a: Account, key: IdentityKey): string | undefined {
  switch (key) {
    case "personalnummer": return a.personalnummer;
    case "email": return a.email;
    default: return a.username;
  }
}

/** Abdeckung, Kollisionen, Personen und FUE eines Schlüssels — Auflösung über resolveIdentity. */
export function identityCoverage(accounts: Account[], persons: Person[], key: IdentityKey, ratio: RatioTable = DEFAULT_RATIO): IdentityCoverage {
  const res = resolveIdentity(accounts, persons, key, ratio);
  const groups = new Map<string, Set<string>>();
  const groupSize = new Map<string, number>();
  let fachlich = 0, withKey = 0;
  for (const a of accounts) {
    if (!a.personId.startsWith("P-")) continue;
    fachlich++;
    const norm = normalize(key, keyRaw(a, key));
    if (norm === null) continue;
    withKey++;
    const s = groups.get(norm);
    if (s) s.add(a.personId); else groups.set(norm, new Set([a.personId]));
    groupSize.set(norm, (groupSize.get(norm) ?? 0) + 1);
  }
  let collisions = 0, collisionAccounts = 0;
  for (const [k, s] of groups) if (s.size > 1) { collisions++; collisionAccounts += groupSize.get(k) ?? 0; }
  return {
    key, accounts: fachlich, withKey,
    coveragePct: fachlich > 0 ? (withKey / fachlich) * 100 : 0,
    collisions, collisionAccounts,
    recognizedPersons: res.recognizedPersons, fueTotal: res.fueTotal,
  };
}
